// src/components/ui/MagicButton.jsx
import React, { useState } from 'react';
import { Button, CircularProgress, styled } from '@mui/material';
import { keyframes } from '@mui/system';
import PropTypes from 'prop-types';

// Keyframes are now defined in the theme - reference only
// This ensures consistency with global animations defined in theme/components.js
const glowEffect = keyframes`
  0% { box-shadow: 0 0 5px rgba(242, 197, 38, 0.4), 0 0 10px rgba(242, 197, 38, 0.1); }
  50% { box-shadow: 0 0 10px rgba(242, 197, 38, 0.6), 0 0 20px rgba(242, 197, 38, 0.3); }
  100% { box-shadow: 0 0 5px rgba(242, 197, 38, 0.4), 0 0 10px rgba(242, 197, 38, 0.1); }
`;

// Keyframes for the magical reveal effect
const revealEffect = keyframes`
  0% { transform: scale(0.9); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

// Create a subtle float animation - reference only
// This is also defined at the theme level in componentTokens.animations
const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-5px);
  }
`;

// Styled Button component with magical effects
const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => !['magical', 'floating'].includes(prop),
})(({ theme, magical, floating, color, variant, disabled }) => ({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: theme?.aghrba?.tokens?.borderRadius?.button || 30,
  padding: theme?.aghrba?.tokens?.padding?.button?.small || '14px 20px',
  height: theme?.aghrba?.tokens?.buttonHeight?.small || 48.5,
  transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
  cursor: disabled ? 'not-allowed' : 'pointer',
  pointerEvents: disabled ? 'none' : 'auto',
  
  
  // Apply magical reveal animation
  ...(magical && {
    animation: theme?.aghrba?.tokens?.animations?.reveal || `${revealEffect} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)`,
  }),
  
  // Apply floating animation if prop is true
  ...(floating && {
    animation: theme?.aghrba?.tokens?.animations?.float || `${float} 3s ease-in-out infinite`,
  }),
  
  // Apply glow effect for magical buttons
  ...(magical && !floating && {
    animation: theme?.aghrba?.tokens?.animations?.glow || `${glowEffect} 2s infinite`,
  }),
  
  // Shine effect removed - was causing hover flickering issues
  
  '&:active': {
    transform: 'translateY(1px)',
    transition: 'transform 0.1s ease-out',
  },
  
  '&:hover': {
    transform: disabled || floating ? 'none' : 'translateY(-1px)',
    transition: 'transform 0.15s ease-out, box-shadow 0.15s ease-out',
  },
  
  // Default styles for contained primary
  '&.MuiButton-containedPrimary': {
    background: theme?.aghrba?.gradients?.primaryButton || 
      `linear-gradient(45deg, ${theme.palette?.primary?.dark || '#0D2E4D'} 0%, ${theme.palette?.primary?.main || '#1C4670'} 100%)`,
    boxShadow: theme?.aghrba?.tokens?.shadows?.primaryButton || '0 4px 10px rgba(13, 46, 77, 0.3)',
    '&:hover': {
      boxShadow: theme?.aghrba?.tokens?.shadows?.primaryButtonHover || '0 6px 12px rgba(13, 46, 77, 0.4)',
      background: `linear-gradient(45deg, ${theme.palette?.primary?.main || '#1C4670'} 0%, ${theme.palette?.primary?.light || '#2B5885'} 100%)`,
    }
  },
  
  // Default styles for contained secondary
  '&.MuiButton-containedSecondary': {
    background: theme?.aghrba?.gradients?.secondaryButton || 
      `linear-gradient(45deg, ${theme.palette?.secondary?.dark || '#D9AD1F'} 0%, ${theme.palette?.secondary?.main || '#f2c526'} 100%)`,
    boxShadow: theme?.aghrba?.tokens?.shadows?.secondaryButton || '0 4px 10px rgba(242, 197, 38, 0.3)',
    '&:hover': {
      boxShadow: theme?.aghrba?.tokens?.shadows?.secondaryButtonHover || '0 6px 12px rgba(242, 197, 38, 0.4)',
      background: `linear-gradient(45deg, ${theme.palette?.secondary?.main || '#f2c526'} 0%, ${theme.palette?.secondary?.light || '#FFD859'} 100%)`,
    }
  },
}));

// The MagicButton component - maintaining same API as original
const MagicButton = React.forwardRef(({
  children,
  magical = false,
  floating = false,
  loading = false,
  loadingPosition = 'center',
  startIcon,
  endIcon,
  disabled,
  fullWidth,
  onClick,
  ...props
}, ref) => {
  const [rippleEffect, setRippleEffect] = useState(false);
  
  // Handle the click with magical effect
  const handleClick = (event) => {
    if (!disabled && !loading) {
      setRippleEffect(true);
      setTimeout(() => setRippleEffect(false), 500);
      
      if (onClick) {
        onClick(event);
      }
    }
  };
  
  // Custom loading elements based on position
  const getLoadingElement = () => {
    return (
      <CircularProgress
        size={24}
        color="inherit"
        sx={{
          position: loadingPosition === 'center' ? 'absolute' : 'static',
          ...(loadingPosition === 'start' && { mr: 1 }),
          ...(loadingPosition === 'end' && { ml: 1 }),
        }}
      />
    );
  };
  
  // Conditional loading with child visibility
  const renderChildren = () => {
    if (loading && loadingPosition === 'center') {
      return <span style={{ visibility: 'hidden' }}>{children}</span>;
    }
    return children;
  };
  
  return (
    <StyledButton
      ref={ref}
      magical={magical}
      floating={floating}
      fullWidth={fullWidth}
      startIcon={loading && loadingPosition === 'start' 
        ? getLoadingElement() 
        : startIcon}
      endIcon={loading && loadingPosition === 'end' 
        ? getLoadingElement() 
        : endIcon}
      disabled={disabled || loading}
      onClick={handleClick}
      {...props}
    >
      {loading && loadingPosition === 'center' && getLoadingElement()}
      {renderChildren()}
    </StyledButton>
  );
});

MagicButton.propTypes = {
  children: PropTypes.node.isRequired,
  magical: PropTypes.bool,
  floating: PropTypes.bool,
  loading: PropTypes.bool,
  loadingPosition: PropTypes.oneOf(['center', 'start', 'end']),
  startIcon: PropTypes.node,
  endIcon: PropTypes.node,
  disabled: PropTypes.bool,
  fullWidth: PropTypes.bool,
  onClick: PropTypes.func,
};

MagicButton.displayName = 'MagicButton';

export default MagicButton;